import { secondsToFormattedLabel2 } from '../../utils/date';
import { countCountries } from '../../utils/geo';
import { forceBrowserDownload } from '../../utils/download';
import {
  normalizeBrowsers,
  normalizeOSs,
  normalizePlatform,
  regionNameFromCode,
  normalizeHostData,
  computeInsights,
} from '../../utils/analytics';

class ViewerAnalyticsController {
  constructor(
    AnalyticsResource,
    AnalyticsExportResource,
    Broadcast,
    Channel,
    ApiConfig,
    $state,
    $rootScope,
    $q,
    toaster,
    $http,
    $scope
  ) {
    'ngInject';

    this.AnalyticsResource = AnalyticsResource;
    this.AnalyticsExportResource = AnalyticsExportResource;
    this.Broadcast = Broadcast;
    this.Channel = Channel;
    this.toaster = toaster;
    this.$q = $q;
    this.canDoAnalytics =
      $rootScope.account.feature_flags.analytics &&
      $rootScope.user.can('read', 'viewer-analytics/*');
    if (!this.canDoAnalytics) {
      return;
    }

    this.report = $state.params.report;
    this.id = $state.params.id;
    this.account = $rootScope.account;
    this.channels = Channel.query({ l: 1000 });

    this.regionDictPromise = $http.get('/img/region_codes.json').then((response) => {
      this.regionDict = response.data;
    });

    // Analytics exports are restricted to <= 50,000 views.
    this.exportViewLimit = 50000;

    // isExporting designates whether an export request is actively being processed.
    this.isExporting = false;

    const commonPieChartOptions = {
      aspectRatio: 2.5,
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          fontSize: 12,
          boxWidth: 12,
          padding: 5,
        },
      },
    };
    const pieChartLabel = (point, context) => {
      const views = context.datasets[point.datasetIndex].data[point.index];
      const totalViews = _.sum(context.datasets[point.datasetIndex].data);

      const label = context.labels[point.index];
      const valueString = `${views} view${views == 1 ? '' : 's'}`;
      let percentString = '';
      if (totalViews > 0) {
        percentString = ` (${Math.round((100.0 * views) / totalViews)}%)`;
      }

      return `${label}: ${valueString}${percentString}`;
    };
    const qualityPieChartLabel = (point, context) => {
      const duration = context.datasets[point.datasetIndex].data[point.index];
      const totalDuration = _.sum(context.datasets[point.datasetIndex].data);

      const label = context.labels[point.index];
      const valueString = secondsToFormattedLabel2(duration);
      let percentString = '';
      if (totalDuration > 0) {
        percentString = ` (${Math.round((100.0 * duration) / totalDuration)}%)`;
      }

      return `${label}: ${valueString}${percentString}`;
    };

    this.pieChartOptions = _.extend({}, commonPieChartOptions, {
      tooltips: {
        callbacks: {
          label: pieChartLabel,
        },
      },
    });

    this.qualityPieChartOptions = _.extend({}, commonPieChartOptions, {
      tooltips: {
        callbacks: {
          label: qualityPieChartLabel,
        },
      },
    });

    this.loadingPromises = [this.channels.$promise, this.regionDictPromise];

    var preset = $state.params.preset;
    if (!preset) {
      if (this.report == 'broadcasts') {
        preset = 'all_time';
      } else {
        preset = 'past_week';
      }
    }

    this.selectDatePreset(preset, $state.params);
  }

  selectDatePreset(preset, args = {}) {
    const { toaster } = this;

    if (this.datePreset == preset && preset != 'custom') {
      return;
    }

    this.datePreset = preset;
    switch (preset) {
      case 'all_time':
        this.since = moment(); //<-- XXX: this needs reset below after we get the broadcast resource
        this.until = moment();
        // Ensure equal number of days in comparison period...
        var days = this.until.diff(this.since, 'days');
        this.compareSince = null;
        this.compareUntil = null;
        this.graphGranularity = 'day';
        break;
      case 'custom':
        this.since = moment(args.since);
        this.until = moment(args.until);
        // Ensure equal number of days in comparison period...
        var days = this.until.diff(this.since, 'days');
        this.compareSince = moment(this.since).clone().subtract(days, 'days');
        this.compareUntil = this.compareSince.clone().add(days, 'days');
        this.graphGranularity = 'day';
        break;
      case 'past_week':
        // subtract 6 days, not the full 7, as it's inclusive of the current day.
        this.since = moment().subtract(6, 'days');
        this.until = moment();
        // Ensure equal number of days in comparison period...
        var days = this.until.diff(this.since, 'days');
        this.compareSince = moment(this.since).clone().subtract(6, 'days');
        this.compareUntil = this.compareSince.clone().add(days, 'days');
        this.graphGranularity = 'day';
        break;
      case 'past_month':
        this.since = moment().subtract(1, 'month');
        this.until = moment();
        // Ensure equal number of days in comparison period...
        var days = this.until.diff(this.since, 'days');
        this.compareSince = moment(this.since).clone().subtract(1, 'month');
        this.compareUntil = this.compareSince.clone().add(days, 'days');
        this.graphGranularity = 'day';
        break;
      case 'past_year':
        this.since = moment().subtract(1, 'year');
        this.until = moment();
        // Ensure equal number of days in comparison period...
        var days = this.until.diff(this.since, 'days');
        this.compareSince = moment(this.since).clone().subtract(1, 'year');
        this.compareUntil = this.compareSince.clone().add(days, 'days');
        this.graphGranularity = 'month';
        break;
      case 'this_cal_month':
        this.since = moment().startOf('month');
        this.until = moment();
        // Get entire previous month as comparison, even if it has more days
        this.compareSince = moment(this.since).clone().subtract(1, 'month').startOf('month');
        this.compareUntil = moment(this.since).clone().subtract(1, 'month').endOf('month');
        this.graphGranularity = 'day';
        break;
      case 'last_cal_month':
        this.since = moment().subtract(1, 'month').startOf('month');
        this.until = moment().subtract(1, 'month').endOf('month');
        // Get entire previous month as comparison, even if it has more days
        this.compareSince = moment().subtract(2, 'month').startOf('month');
        this.compareUntil = moment().subtract(2, 'month').endOf('month');
        this.graphGranularity = 'day';
        break;
      default:
        toaster.pop('warning', 'Unknown date preset');
    }

    // Defaults for custom date picker
    this.customSince = this.since.clone().toDate();
    this.customUntil = this.until.clone().toDate();

    // Normalize to DateTime objects instead of moment instances
    this.since = this.since.toDate();
    this.until = this.until.toDate();
    this.compareSince = this.compareSince ? this.compareSince.toDate() : null;
    this.compareUntil = this.compareUntil ? this.compareUntil.toDate() : null;

    this.dateDropdownOpened = false;

    this.reload();
  }

  reload() {
    const { account, report, id, Broadcast, Channel } = this;

    switch (report) {
      case 'broadcasts':
        this.broadcast = Broadcast.get({ id: id }, (broadcast) => {
          if (this.datePreset == 'all_time') {
            this.since = moment(this.broadcast.starts_at);
            this.customSince = this.since.clone().toDate();
            this.since = this.since.toDate();
          }
          this.selectedChannelName = broadcast.name;
          this.queryAnalytics(broadcast.channel_id);
        });
        break;
      case 'channels':
        Channel.get({ id: id }, (channel) => (this.selectedChannelName = channel.name));
        this.queryAnalytics(id);
        break;
      default:
        this.selectedChannelName = 'Entire Account';
        this.queryAnalytics(account.channel_id);
        break;
    }
  }

  hasViews() {
    if (!this.analytics) return false;
    return this.analytics.total_view_count > 0;
  }

  // Analytics exports are restricted based on the # of views in the current report.
  canExport() {
    return this.hasViews() && this.analytics.total_view_count <= this.exportViewLimit;
  }

  queryAnalytics(channel_id) {
    const { $q, toaster, AnalyticsResource } = this;

    // TODO: configurable timezone
    // NOTE: the time portion is ignored by API, only the date is used, but we need the
    // time so that you can pass the timezone in. Time logic is: since <= date < until
    this.analytics = AnalyticsResource.get({
      channel_id: channel_id,
      since: moment(this.since)
        .local()
        .set({ h: 0, m: 0, s: 0, ms: 0 })
        .format('YYYY-MM-DDTHH:mm:ssZ'),
      until: moment(this.until)
        .clone()
        .add(1, 'day')
        .local()
        .set({ h: 0, m: 0, s: 0, ms: 0 })
        .format('YYYY-MM-DDTHH:mm:ssZ'),
    });

    if (this.datePreset == 'all_time') {
      this.analyticsCompare = {};
    } else {
      this.analyticsCompare = AnalyticsResource.get({
        channel_id: channel_id,
        since: moment(this.compareSince)
          .local()
          .set({ h: 0, m: 0, s: 0, ms: 0 })
          .format('YYYY-MM-DDTHH:mm:ssZ'),
        until: moment(this.compareUntil)
          .clone()
          .add(1, 'day')
          .local()
          .set({ h: 0, m: 0, s: 0, ms: 0 })
          .format('YYYY-MM-DDTHH:mm:ssZ'),
      });
    }

    this.loadingPromises = [
      this.analytics.$promise,
      this.analyticsCompare.$promise,
      this.regionDictPromise,
    ];

    $q.all([this.analytics.$promise, this.analyticsCompare.$promise, this.regionDictPromise])
      .then(() => {
        // If no collected view data for selected option; API returns null. We coerce into something
        // that avoids explosion or other special handling by UI.
        const dummy = () => {
          return {
            live_view_count: 0,
            recorded_view_count: 0,
            live_view_seconds: 0,
            recorded_view_seconds: 0,
            unique_viewer_count: 0,
            viewed_broadcast_count: 0,
            daily_view_counts: [],
            weekday_hourly_view_counts: [],
            view_durations_by_quality: {},
            view_counts_by_host: {},
            most_popular_broadcasts: [],
          };
        };
        if (this.analytics.live_view_count === undefined) {
          this.analytics = angular.extend(this.analytics, dummy());
        }
        if (this.analyticsCompare.live_view_count === undefined) {
          this.analyticsCompare = angular.extend(this.analyticsCompare, dummy());
        }

        // glue on computed fields
        this.analytics.total_view_count =
          this.analytics.live_view_count + this.analytics.recorded_view_count;
        this.analytics.total_view_duration =
          this.analytics.live_view_seconds + this.analytics.recorded_view_seconds;
        this.analytics.avg_view_duration =
          this.analytics.total_view_duration / this.analytics.total_view_count;
        if (this.analytics.live_buffer_seconds == 0 || this.analytics.live_view_seconds == 0) {
          this.analytics.live_rebuffer_pct = 0;
        } else {
          this.analytics.live_rebuffer_pct =
            100 *
            (this.analytics.live_buffer_seconds /
              (this.analytics.live_buffer_seconds + this.analytics.live_view_seconds));
        }
        if (
          this.analytics.recorded_buffer_seconds == 0 ||
          this.analytics.recorded_view_seconds == 0
        ) {
          this.analytics.recorded_rebuffer_pct = 0;
        } else {
          this.analytics.recorded_rebuffer_pct =
            100 *
            (this.analytics.recorded_buffer_seconds /
              (this.analytics.recorded_buffer_seconds + this.analytics.recorded_view_seconds));
        }

        // shared graph config
        this.barOptions = {
          barPercentage: 0.95,
          categoryPercentage: 0.5,
          legend: { display: false },
          scales: {
            xAxes: [
              {
                gridLines: { display: false },
                position: 'bottom',
                ticks: {
                  fontSize: 10,
                },
              },
            ],
          },
        };

        // compute graph data
        this.viewsLabels = [];
        this.viewsData = [[]];
        this.viewsOptions = {
          legend: { display: false },
          scales: {
            xAxes: [
              {
                type: 'time',
                time: {
                  unit: 'day',
                  displayFormats: {
                    day: 'MMM Do',
                  },
                },
                //categoryPercentage: 0.20, //1 every 5 labels
                gridLines: { display: false },
                position: 'bottom',
                ticks: {
                  fontSize: 11,
                  fontFamily: 'Arial, sans-serif',
                  //maxRotation: 0,
                  autoSkip: true,
                },
              },
            ],
            //yAxes: [{
            //  gridLines: { display: false }
            //}]
          },
          tooltips: {
            callbacks: {
              title: (items, data) => {
                return this.viewsLabels[items[0].index].format('ddd MMM Do');
              },
            },
          },
        };
        (this.analytics.daily_view_counts || []).forEach((c, i) => {
          this.viewsLabels.push(moment(this.since).clone().add(i, 'day'));
          this.viewsData[0].push(c);
        });

        this.qualityData = _.values(this.analytics.view_durations_by_quality);
        this.qualityLabels = _.keys(this.analytics.view_durations_by_quality).map(
          (l) => l || 'Not reported'
        );

        // Compute insights before we mutate the view_counts_by_* dictionaries
        this.insights = computeInsights(null, this.analytics);

        // Better formatting on hosts
        this.analytics.hostData = normalizeHostData(this.analytics);

        // NOTE: this has to happen before we mutate the view_counts_by_* dictionaries below
        var mobile = normalizePlatform(
          this.analytics.view_counts_by_os,
          this.analytics.view_counts_by_browser
        );
        this.mobileData = _.values(mobile);
        this.mobileLabels = _.keys(mobile);

        this.analytics.view_counts_by_browser = normalizeBrowsers(
          this.analytics.view_counts_by_browser
        );
        var sortedBrowsers = _(this.analytics.view_counts_by_browser)
          .pairs()
          .sortBy(1)
          .reverse()
          .value();
        this.browserData = sortedBrowsers.map((p) => p[1]);
        this.browserLabels = sortedBrowsers.map((p) => p[0]);

        this.analytics.view_counts_by_os = normalizeOSs(this.analytics.view_counts_by_os);
        var sortedOS = _(this.analytics.view_counts_by_os).pairs().sortBy(1).reverse().value();
        this.osData = sortedOS.map((p) => p[1]);
        this.osLabels = sortedOS.map((p) => p[0]);

        this.regionData = [];
        _.each(this.analytics.view_counts_by_region_code, (count, code) => {
          var label = regionNameFromCode(code, this.regionDict);
          this.regionData.push({ code, count, label });
        });
        this.regionData = _.sortBy(this.regionData, 'count');
        this.regionData.reverse();
        this.regionDataCount = 10;
        this.regionDataSlice = this.regionData.slice(0, this.regionDataCount);

        if (this.analytics.live_view_count || this.analytics.recorded_view_count) {
          var totalViewCount = this.analytics.live_view_count + this.analytics.recorded_view_count;
          this.liveData = [
            this.analytics.live_view_count / totalViewCount,
            this.analytics.recorded_view_count / totalViewCount,
          ];
        } else {
          this.liveData = null;
        }

        this.bubbleGraphRows = [];
        var maxWeekDayHourVal = 1;
        _.range(7).forEach((d, i) => {
          let day = { dayIndex: d, hours: [] };
          _.range(24).forEach((h, i) => {
            var viewCount = (this.analytics.weekday_hourly_view_counts || [])[d * 24 + h] || 0;
            maxWeekDayHourVal = Math.max(viewCount, maxWeekDayHourVal);
            day.hours.push(viewCount);
          });
          this.bubbleGraphRows.push(day);
        });
        this.bubbleGraphScaleFactor = 0.05 * (100 / maxWeekDayHourVal);

        // compute comparison graph data structure
        this.viewsVsLabels = [];
        this.viewsVsTooltips = [];
        _.range(
          Math.max(
            (this.analytics.daily_view_counts || []).length,
            this.analyticsCompare.daily_view_counts.length
          )
        ).forEach((i) => {
          var curr = moment(this.since).clone().add(i, 'day');
          var comp = moment(this.compareSince).clone().add(i, 'day');
          this.viewsVsLabels.push(curr);
          this.viewsVsTooltips.push(`${curr.format('ddd MMM Do')} vs ${comp.format('ddd MMM Do')}`);
        });
        this.viewsVsData = [
          [], //cululative count of current period
          [], //cumulative count of comparison period
        ];
        this.viewsVsOptions = {
          legend: { display: false },
          scales: {
            xAxes: [
              {
                type: 'time',
                time: {
                  unit: 'day',
                  displayFormats: {
                    day: 'MMM Do',
                  },
                },
                //categoryPercentage: 0.20, //1 every 5 labels
                gridLines: { display: false },
                position: 'bottom',
                ticks: {
                  fontSize: 11,
                  fontFamily: 'Arial, sans-serif',
                  //maxRotation: 0,
                  autoSkip: true,
                },
              },
            ],
            //yAxes: [{
            //  gridLines: { display: false }
            //}]
          },
          tooltips: {
            callbacks: {
              title: (items, data) => {
                return this.viewsVsTooltips[items[0].index];
              },
            },
          },
        };

        var sum = 0;
        (this.analytics.daily_view_counts || []).forEach((c) =>
          this.viewsVsData[0].push((sum += c))
        );
        sum = 0;
        (this.analyticsCompare.daily_view_counts || []).forEach((c) =>
          this.viewsVsData[1].push((sum += c))
        );

        // compute comparison stats
        this.analyticsCompare.total_view_count =
          this.analyticsCompare.live_view_count + this.analyticsCompare.recorded_view_count;
        this.analyticsCompare.total_view_duration =
          this.analyticsCompare.live_view_seconds + this.analyticsCompare.recorded_view_seconds;
        this.analyticsCompare.avg_view_duration =
          this.analyticsCompare.total_view_duration / this.analyticsCompare.total_view_count;

        this.computeAchievements();
      })
      .catch((err) => {
        console.error(err);
        toaster.pop('warning', 'Unable to load analytics. Please refresh the page and try again.');
      });
  }

  // downloadAnalytics handles click events from the "export analytics" link on the reports/viewer page.
  downloadAnalytics() {
    const { account, report, id, Broadcast, Channel } = this;

    // block download requests if: exports are not allowed (i.e. the date range exceeds the allowed limit);
    // or if an export is already in progress.
    if (!this.canExport() || this.isExporting) return;

    // designate that we're now actively processing an export request.
    this.isExporting = true;

    switch (report) {
      case 'broadcasts':
        this.broadcast = Broadcast.get({ id: id }, (broadcast) => {
          if (this.datePreset == 'all_time') {
            this.since = moment(this.broadcast.starts_at);
            this.customSince = this.since.clone().toDate();
            this.since = this.since.toDate();
          }
          this.exportAnalytics(broadcast.channel_id);
        });
        break;
      case 'channels':
        Channel.get({ id: id }, (channel) => (this.selectedChannelName = channel.name));
        this.exportAnalytics(id);
        break;
      default:
        this.exportAnalytics(account.channel_id);
        break;
    }
  }

  // exportAnalytics calls on the viewer_analytics_service to get detailed view analytics for the
  // currently-viewed analytics and prompts a csv file download.
  exportAnalytics(channel_id) {
    const { AnalyticsExportResource, toaster } = this;

    // TODO: configurable timezone
    // NOTE: the time portion is ignored by API, only the date is used, but we need the
    // time so that you can pass the timezone in. Time logic is: since <= date < until
    const params = {
      channel_id: channel_id,
      since: moment(this.since)
        .local()
        .set({ h: 0, m: 0, s: 0, ms: 0 })
        .format('YYYY-MM-DDTHH:mm:ssZ'),
      until: moment(this.until)
        .clone()
        .add(1, 'day')
        .local()
        .set({ h: 0, m: 0, s: 0, ms: 0 })
        .format('YYYY-MM-DDTHH:mm:ssZ'),
    };
    AnalyticsExportResource.render(
      params,
      (response) => {
        // got our response, so the export is done processing.
        this.isExporting = false;

        const disposition = response.headers['content-disposition'];
        const filename = disposition.match(/filename=(\"?)(.+)\1/)[2];
        forceBrowserDownload(URL.createObjectURL(response.blob), filename);
      },
      (err) => {
        // error, but the export is done processing.
        this.isExporting = false;

        console.error(err);
        toaster.pop(
          'warning',
          'Unable to export analytics',
          'There was an issue exporting your analytics. Please reload and try again.'
        );
      }
    );
  }

  loadMoreRegionData() {
    this.regionDataCount += 10;
    this.regionDataSlice = this.regionData.slice(0, this.regionDataCount);
  }

  computeAchievements() {
    this.achievements = {
      // was total view count over 1000?
      views_over_1000: this.analytics.total_view_count > 1000,

      // was total view count over 10000?
      views_over_10000: this.analytics.total_view_count > 10000,

      // were 75% of views from a single time-of-day & day-of-week combo?
      hotspot:
        Math.max(this.analytics.weekday_hourly_view_counts) >
        0.75 *
          (this.analytics.weekday_hourly_view_counts || []).reduce((a, b) => {
            return a + b;
          }, 0),

      // were more than 75% of views at 720 or 1080 quality?
      hifi:
        ((this.analytics.view_durations_by_quality || {})['720p'] || 0) +
          ((this.analytics.view_durations_by_quality || {})['1080p'] || 0) >
        0.75 * this.analytics.total_view_count,

      // were live views more than 95% of total?
      do_it_live: this.analytics.live_view_count / this.analytics.total_view_count > 0.95,

      // was avg view duration at least 10 min?
      engaged: this.analytics.avg_view_duration > 10 * 60,

      // did total view count more than double compared to prior?
      daily_double: this.analytics.total_view_count > 2 * this.analyticsCompare.total_view_count,

      // multiple countries
      world_traveler: countCountries(this.analytics.view_counts_by_region_code) > 1,
    };
  }

  bubbleGraphX(day, hr, hrIndex, dayIndex) {
    // based on 100x50 grid
    return 100 * ((day.dayIndex + 0.5) / 7);
  }

  bubbleGraphY(day, hr, hrIndex, dayIndex) {
    // based on 100x50 grid
    return 50 * ((hrIndex + 0.5) / 24);
  }

  bubbleGraphR(day, hr, hrIndex, dayIndex) {
    // based on 100x100 grid
    return Math.min(5, hr * this.bubbleGraphScaleFactor);
  }

  bubbleGraphTitle(day, hr, hrIndex, dayIndex) {
    var hrLabel;
    if (hrIndex > 12) {
      hrLabel = `${hrIndex - 12}pm`;
    } else if (hrIndex == 12) {
      hrLabel = '12pm';
    } else if (hrIndex == 0) {
      hrLabel = '12am';
    } else {
      hrLabel = `${hrIndex}am`;
    }
    var dayLabel = [
      'Sundays',
      'Mondays',
      'Tuesdays',
      'Wednesdays',
      'Thursdays',
      'Fridays',
      'Saturdays',
    ][dayIndex];
    return `${hr} (${dayLabel} at ${hrLabel})`;
  }
}

export default ViewerAnalyticsController;



// WEBPACK FOOTER //
// ./app/pages/reports/viewer.js